"use client";

import type * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { useAppShell } from "./app-shell-context";

/**
 * The shell's toggle affordance: opens/closes the mobile nav drawer (and the
 * desktop pane, though the module-menu header owns that on desktop). Ghost icon
 * button, mirroring the base sidebar trigger.
 */
export function AppShellTrigger({
  className,
  onClick,
  size,
  ...props
}: React.ComponentProps<typeof Button>): React.ReactElement {
  const { toggleSidebar, tier, open, openMobile, openOverlay } = useAppShell();
  // Mirror the same per-tier state toggleSidebar flips, so the icon shows the
  // drawer's current state at every tier.
  const sidebarOpen =
    tier === "compact" ? openMobile : tier === "medium" ? openOverlay : open;

  return (
    <Button
      aria-label="Toggle navigation"
      // Existing shell placements keep the compact 28 px face. A canvas dock
      // can request a larger Button rung without this default overriding it.
      className={cn(size == null && "size-7", className)}
      data-slot="app-shell-trigger"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      size={size ?? "icon"}
      variant="ghost"
      {...props}
    >
      {/* Registry artwork is swapped: `drawer-closed-*` draws the open drawer. */}
      <Icon name={sidebarOpen ? "drawer-closed-left" : "drawer-open-left"} />
    </Button>
  );
}
